import * as d3 from 'd3'
import { ScaleLinear, ScaleTime } from 'd3'

interface StackedBarData {
    date: Date
    requests: Array<{ label: string; requests: number }>
}

interface TooltipData {
    date: Date
    totalRequests: number
    breakdown: Array<{ label: string; requests: number; color: string }>
}

interface ChartDimensions {
    width: number
    height: number
    margin: { top: number; right: number; bottom: number; left: number }
}

export const billingRequestStats = (svgElement: SVGElement, width: number, height: number, data: StackedBarData[]): void => {
    // Validate input data
    if (!data || data.length === 0) {
        console.warn('No data provided to chart')
        return
    }

    // Filter out invalid data entries
    const validData = data.filter(d => d && d.date && Array.isArray(d.requests))

    if (validData.length === 0) {
        console.warn('No valid data entries found')
        return
    }

    // Clear any existing content
    d3.select(svgElement)
        .selectAll('*')
        .remove()

    const dimensions: ChartDimensions = {
        width,
        height,
        margin: { top: 20, right: 30, bottom: 40, left: 50 },
    }

    const innerWidth = dimensions.width - dimensions.margin.left - dimensions.margin.right
    const innerHeight = dimensions.height - dimensions.margin.top - dimensions.margin.bottom

    // Get all unique labels for stacking
    const requestLabels = [...new Set(validData.flatMap(d => d.requests.map(r => r.label)))]

    // Color scale for different request types
    const colorScale = d3
        .scaleOrdinal<string>()
        .domain(requestLabels)
        .range(d3.schemeCategory10)

    // Transform data with safety checks
    const stackData = validData.map(d => {
        const item: any = { date: d.date }

        // Safely process requests array
        if (d.requests && Array.isArray(d.requests)) {
            d.requests.forEach(request => {
                if (request && request.label && typeof request.requests === 'number') {
                    item[request.label] = request.requests
                }
            })
        }

        // Ensure all labels have a value (0 if missing)
        requestLabels.forEach(label => {
            if (!(label in item)) {
                item[label] = 0
            }
        })
        return item
    })

    // Create stack generator
    const stack = d3
        .stack<any>()
        .keys(requestLabels)
        .order(d3.stackOrderNone)
        .offset(d3.stackOffsetNone)

    const stackedData = stack(stackData)

    // Calculate bar width and positioning
    const barWidth = Math.max(1, (innerWidth * 0.8) / validData.length) // Use 80% of available width
    const barSpacing = (innerWidth * 0.2) / (validData.length + 1) // Distribute remaining 20% as spacing

    // Create scales with proper domain and range for bar positioning
    const xScale: ScaleTime<number, number> = d3
        .scaleTime()
        .domain(d3.extent(validData, d => d.date) as [Date, Date])
        .range([barSpacing + barWidth / 2, innerWidth - barSpacing - barWidth / 2])

    const yScale: ScaleLinear<number, number> = d3
        .scaleLinear()
        .domain([0, d3.max(stackedData, layer => d3.max(layer, d => d[1])) || 0])
        .range([innerHeight, 0])

    // Create SVG container
    const svg = d3
        .select(svgElement)
        .attr('width', dimensions.width)
        .attr('height', dimensions.height)

    const g = svg.append('g').attr('transform', `translate(${dimensions.margin.left},${dimensions.margin.top})`)

    // Create tooltip with better structure
    const tooltip = svg
        .append('g')
        .attr('class', 'svg-tooltip')
        .style('pointer-events', 'none')
        .style('opacity', 0)

    const tooltipRect = tooltip
        .append('rect')
        .attr('fill', 'rgba(255, 255, 255, 0.9)')
        .attr('rx', 6)
        .attr('stroke', '#aaa')
        .attr('stroke-width', 1)

    // Create bars for each layer
    const layers = g
        .selectAll('.layer')
        .data(stackedData)
        .enter()
        .append('g')
        .attr('class', 'layer')
        .style('fill', d => colorScale(d.key))

    // Create individual rectangles for each stack segment
    layers
        .selectAll('rect')
        .data(d => d)
        .enter()
        .append('rect')
        .attr('x', (d: any) => {
            if (!d || !d.data || !d.data.date) return 0

            const xPosition = xScale(d.data.date)
            if (!xPosition) return 0

            return xPosition - barWidth / 2
        })
        .attr('y', (d: any) => {
            if (!d || typeof d[1] !== 'number') return 0
            return yScale(d[1]) || 0
        })
        .attr('height', (d: any) => {
            if (!d || typeof d[0] !== 'number' || typeof d[1] !== 'number') return 0
            const yPosition0 = yScale(d[0])
            const yPosition1 = yScale(d[1])

            if (!yPosition0 || !yPosition1) return 0

            return yPosition0 - yPosition1
        })
        .attr('width', barWidth)

    // Create invisible overlay bars for day-level interaction
    const dayGroups = g
        .selectAll('.day-group')
        .data(validData)
        .enter()
        .append('g')
        .attr('class', 'day-group')

    dayGroups
        .append('rect')
        .attr('class', 'day-overlay')
        .attr('x', d => (xScale(d.date) || 0) - barWidth / 2)
        .attr('y', 0)
        .attr('width', barWidth)
        .attr('height', innerHeight)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mouseenter', d => {
            // Calculate total requests for this day
            const totalRequests = d.requests.reduce((sum, r) => sum + r.requests, 0)

            // Create tooltip data
            const tooltipData: TooltipData = {
                date: d.date,
                totalRequests,
                breakdown: d.requests
                    .filter(r => r.requests > 0)
                    .map(r => ({
                        label: r.label,
                        requests: r.requests,
                        color: colorScale(r.label),
                    }))
                    .sort((a, b) => b.requests - a.requests),
            }

            // Clear previous tooltip content
            tooltip.selectAll('.tooltip-content').remove()

            // Create tooltip content group
            const tooltipContent = tooltip.append('g').attr('class', 'tooltip-content')

            const padding = 16
            const lineHeight = 16
            let yPos = padding

            // Add date header
            tooltipContent
                .append('text')
                .attr('x', padding)
                .attr('y', yPos)
                .attr('fill', 'black')
                .attr('font-family', 'Arial, sans-serif')
                .attr('font-size', '13px')
                .attr('font-weight', 'bold')
                .text(d3.timeFormat('%Y-%m-%d')(tooltipData.date))

            yPos += lineHeight + 4

            // Add total line
            tooltipContent
                .append('text')
                .attr('x', padding)
                .attr('y', yPos)
                .attr('fill', '#444')
                .attr('font-family', 'Arial, sans-serif')
                .attr('font-size', '12px')
                .text(`Total: ${numberFormat(tooltipData.totalRequests)}`)

            yPos += lineHeight + 6

            // Add breakdown with color indicators
            tooltipData.breakdown.forEach(item => {
                const itemGroup = tooltipContent.append('g')

                // Color indicator circle
                itemGroup
                    .append('circle')
                    .attr('cx', padding + 6)
                    .attr('cy', yPos - 4)
                    .attr('r', 5)
                    .attr('fill', item.color)

                // Label and value
                itemGroup
                    .append('text')
                    .attr('x', padding + 18)
                    .attr('y', yPos)
                    .attr('fill', '#444')
                    .attr('font-family', 'Arial, sans-serif')
                    .attr('font-size', '11px')
                    .text(`${item.label}: ${numberFormat(item.requests)}`)

                yPos += lineHeight
            })

            // Calculate tooltip dimensions and position
            const bbox = tooltipContent.node()?.getBBox()
            if (bbox) {
                const tooltipWidth = bbox.width + padding * 2
                const tooltipHeight = bbox.height + padding

                // Update background
                tooltipRect.attr('width', tooltipWidth).attr('height', tooltipHeight)

                const rect = svgElement.getBoundingClientRect()
                const mouseX = (d3.event.clientX || d3.event.pageX) - rect.left
                const mouseY = (d3.event.clientY || d3.event.pageY) - rect.top

                // Position tooltip with bounds checking
                let tooltipX = mouseX + 15
                let tooltipY = mouseY - 10

                // Keep within SVG bounds
                if (tooltipX + tooltipWidth > dimensions.width) {
                    tooltipX = mouseX - tooltipWidth - 15
                }
                if (tooltipY < 0) {
                    tooltipY = mouseY + 15
                }
                if (tooltipY + tooltipHeight > dimensions.height) {
                    tooltipY = dimensions.height - tooltipHeight - 10
                }

                // Apply positioning
                tooltip.attr('transform', `translate(${tooltipX}, ${tooltipY})`).style('opacity', 1)
            }

            // Highlight all bars for this day
            g.selectAll('.layer rect')
                .filter((rectData: any) => {
                    return rectData && rectData.data && rectData.data.date.getTime() === d.date.getTime()
                })
                .style('opacity', 0.8)
                .style('stroke', '#333')
                .style('stroke-width', 1)
        })
        .on('mouseleave', function() {
            // Hide tooltip
            tooltip.style('opacity', 0)

            // Remove highlight from all bars
            g.selectAll('.layer rect')
                .style('opacity', 1)
                .style('stroke', 'none')
        })

    // Create X axis
    const xAxis = d3
        .axisBottom(xScale)
        .tickFormat(d3.timeFormat('%m/%d'))
        .ticks(Math.min(10, validData.length))

    g.append('g')
        .attr('class', 'x-axis')
        .attr('transform', `translate(0,${innerHeight})`)
        .call(xAxis)
        .selectAll('text')
        .style('text-anchor', 'end')
        .attr('dx', '-.8em')
        .attr('dy', '.15em')
        .attr('transform', 'rotate(-45)')

    // Create Y axis
    const yAxis = d3.axisLeft(yScale).tickFormat(d3.format('.0s'))

    g.append('g')
        .attr('class', 'y-axis')
        .call(yAxis)

    // Add Y axis label
    g.append('text')
        .attr('class', 'y-axis-label')
        .attr('transform', 'rotate(-90)')
        .attr('y', 0 - dimensions.margin.left)
        .attr('x', 0 - innerHeight / 2)
        .attr('dy', '1em')
        .style('text-anchor', 'middle')
        .text('Requests')
}

const numberFormat = (value: number) =>
    new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 1,
        maximumSignificantDigits: 4,
    }).format(value)
